import { useMemo, useCallback } from 'react';
import { ReportType } from '../model';
import IConnectToTeam from '../model/IConnectToTeam';
import useReportingTeamItems from './useReportingTeamItems';

interface UseGroundTeamReportAvailabilityProps {
    reportType: ReportType | undefined;
    reportingTeams: IConnectToTeam[];
    reportingTeamsStatus: 'idle' | 'loading' | 'failed';
    installationTeamId: string;
    canViewAllTeams: boolean | null;
}

export const useGroundTeamReportAvailability = ({
    reportType,
    reportingTeams,
    reportingTeamsStatus,
    installationTeamId,
    canViewAllTeams,
}: UseGroundTeamReportAvailabilityProps) => {
    const { teams: reportingTeamItems, allTeamsOption } =
        useReportingTeamItems(reportingTeams);

    const isGroundTeamRatingsReport =
        reportType === ReportType.GroundTeamRatingsReport;

    const hasReportingTeamsResolved =
        reportingTeamsStatus !== 'loading';

    const currentInstallationTeam = useMemo(() => {
        return reportingTeamItems.find(
            (team) => team.id === installationTeamId
        );
    }, [reportingTeamItems, installationTeamId]);

    const shouldRestrictGroundTeamRatingsReport =
        isGroundTeamRatingsReport &&
        canViewAllTeams === false &&
        hasReportingTeamsResolved &&
        !currentInstallationTeam;

    const teamsWithAll = useMemo(() => {
        if (
            isGroundTeamRatingsReport &&
            canViewAllTeams === false
        ) {
            return currentInstallationTeam
                ? [currentInstallationTeam]
                : [];
        }

        return reportingTeamItems;
    }, [
        reportingTeamItems,
        isGroundTeamRatingsReport,
        canViewAllTeams,
        currentInstallationTeam,
    ]);

    const hasValidSelectedTeam = (selectedTeamId: string | undefined) => {
        if (
            isGroundTeamRatingsReport &&
            canViewAllTeams === false
        ) {
            return Boolean(getDefaultGroundTeamId());
        }

        return selectedTeamId !== undefined;
    };

    const isRestrictedOption = (type: ReportType) => {
        return (
            type === ReportType.GroundTeamRatingsReport &&
            shouldRestrictGroundTeamRatingsReport
        );
    };

    const getDefaultGroundTeamId = useCallback(() => {
        return currentInstallationTeam?.id ?? '';
    }, [currentInstallationTeam]);


    return {
        teamsWithAll,
        shouldRestrictGroundTeamRatingsReport,
        hasValidSelectedTeam,
        isRestrictedOption,
        getDefaultGroundTeamId,
        allTeamsOption,
    };
};